import { Controller } from "@hotwired/stimulus"
import { easepick, RangePlugin, LockPlugin } from '@easepick/bundle'

export default class extends Controller {
    static targets = ["modal", "rangeInput", "startDateInput", "endDateInput", "form", "calendarInline", "rangeButton", "currentRangeLabel", "exportButton"]

    connect() {
        this.initEasepick()
    }

    initEasepick() {
        if (this.picker) this.picker.destroy()

        this.picker = new easepick.create({
            element: this.calendarInlineTarget,
            css: [
                'https://cdn.jsdelivr.net/npm/@easepick/bundle@1.2.1/dist/index.css',
                'https://cdn.jsdelivr.net/npm/@easepick/range-plugin@1.2.1/dist/index.css'
            ],
            inline: true,
            zIndex: 10,
            grid: 2,
            calendars: 2,
            plugins: [RangePlugin, LockPlugin],
            RangePlugin: {
                tooltipNumber(num) { return num - 1 },
                locale: { one: 'day', other: 'days' },
            },
            LockPlugin: {
                minDate: new Date(2000, 0, 1),
                maxDate: new Date(),
            },
            setup: (picker) => {
                picker.on('select', (e) => {
                    const { start, end } = e.detail;
                    this.updateManualInputs(start, end);
                    this.rangeInputTarget.value = 'custom';
                    this.updateActiveState('custom');
                    this.updateRangeLabel(start, end); // Update the text label
                    this.updateExportLink()
                })
            }
        })

        // Set initial date in picker if values exist
        const startVal = this.startDateInputTarget.value
        const endVal = this.endDateInputTarget.value
        if(startVal && endVal) {
            this.picker.setDateRange(new Date(startVal), new Date(endVal))
        }
    }

    updateExportLink() {
        if (!this.hasExportButtonTarget) return

        const range = this.rangeInputTarget.value
        const from = this.element.querySelector('input[name="from"]').value
        const to = this.element.querySelector('input[name="to"]').value

        // Construct the new URL with the current params
        let newUrl = `/charity/analytics.pdf?range=${range}`

        if (range === 'custom') {
            newUrl += `&from=${from}&to=${to}`
        }

        // Update the onclick attribute dynamically
        this.exportButtonTarget.setAttribute('onclick', `window.location.href='${newUrl}'`)
    }

    updateManualInputs(start, end) {
        const options = { month: 'short', day: 'numeric', year: 'numeric' }
        this.startDateInputTarget.value = start.toLocaleDateString('en-US', options)
        this.endDateInputTarget.value = end.toLocaleDateString('en-US', options)
        this.element.querySelector('input[name="from"]').value = this.formatDate(start)
        this.element.querySelector('input[name="to"]').value = this.formatDate(end)
        this.updateExportLink()
    }

    updateRangeLabel(start, end) {
        const options = { month: 'short', day: 'numeric', year: 'numeric' }
        const label = `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`
        if (this.hasCurrentRangeLabelTarget) {
            this.currentRangeLabelTarget.textContent = label
        }
    }

    formatDate(date) {
        const d = new Date(date)
        let month = '' + (d.getMonth() + 1)
        let day = '' + d.getDate()
        const year = d.getFullYear()
        if (month.length < 2) month = '0' + month
        if (day.length < 2) day = '0' + day
        return [year, month, day].join('-')
    }

    selectRangeAndSubmit(event) {
        const range = event.currentTarget.dataset.range
        this.selectRange(range)
        this.submit()
    }

    selectRangeButton(event) {
        const range = event.currentTarget.dataset.range
        this.selectRange(range)
    }

    updateActiveState(activeRange) {
        this.rangeButtonTargets.forEach(btn => {
            const btnRange = btn.dataset.range
            // Reset classes
            btn.className = "px-4 py-2 text-xs font-bold rounded-lg transition-all cursor-pointer"

            if (btnRange === activeRange) {
                // Active State
                if (btnRange === 'custom') {
                    btn.classList.add('bg-primary/5', 'border', 'border-primary/20', 'text-primary', 'flex', 'items-center', 'gap-2')
                } else {
                    btn.classList.add('bg-white', 'shadow-sm', 'text-primary')
                }
            } else {
                // Inactive State
                btn.classList.add('text-slate-500', 'hover:text-slate-700', 'hover:bg-slate-50')
                if (btnRange === 'custom') btn.classList.add('flex', 'items-center', 'gap-2')
            }
        })
    }

    selectRange(range) {
        let start = new Date()
        let end = new Date()
        start.setHours(0,0,0,0)
        end.setHours(23,59,59,999)

        switch (range) {
            case 'today': break
            case 'yesterday':
                start.setDate(start.getDate() - 1); end.setDate(end.getDate() - 1); break
            case '7_days':
                start.setDate(start.getDate() - 7); break
            case '30_days':
                start.setDate(start.getDate() - 30); break
            case 'this_month':
                start.setDate(1); break
            case 'last_month':
                start.setMonth(start.getMonth() - 1); start.setDate(1)
                end.setDate(0); end.setHours(23, 59, 59, 999)
                break
            case 'last_year':
                start.setFullYear(start.getFullYear() - 1); start.setMonth(0); start.setDate(1)
                end.setFullYear(end.getFullYear() - 1); end.setMonth(11); end.setDate(31)
                break
            case 'quarter':
                const quarter = Math.floor((start.getMonth() / 3))
                start.setMonth(quarter * 3); start.setDate(1)
                break
        }

        if (this.picker) {
            this.picker.setDateRange(start, end) // Easepick updates visualization
        }

        // Explicitly update inputs and label
        this.updateManualInputs(start, end)
        this.updateRangeLabel(start, end)

        this.rangeInputTarget.value = range
        this.updateActiveState(range)
        this.updateExportLink()
    }

    openModal() { this.modalTarget.classList.remove('hidden'); this.modalTarget.classList.add('flex') }
    closeModal() { this.modalTarget.classList.add('hidden'); this.modalTarget.classList.remove('flex') }

    submit() {
        this.closeModal()
        this.formTarget.requestSubmit()
    }

    disconnect() {
        if (this.picker) this.picker.destroy()
    }
}